import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import FormDialog from '@sayyesschool/frontend/components/form-dialog';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import UsersSearch from '@sayyesschool/frontend/components/users-search';
import { useLearners } from '@sayyesschool/frontend/features/learners';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';

import LearnerForm from '@/components/learners/learner-form';
import LearnersTable from '@/components/learners/learners-table';

export default function LearnersPage({ history }) {
    const location = useLocation();
    const navigate = useNavigate();
    const active = location.search.includes('active');

    const [learners, actions] = useLearners(active ? { active } : undefined);

    const [learner, setLearner] = useState();
    const [isLoading, setLoading] = useState(false);
    const [isFormOpen, toggleFormOpen] = useBoolean(false);
    const [isConfirmationDialogOpen, toggleConfirmationDialogOpen] = useBoolean(false);

    const handleTabsChange = useCallback((_, value) => {
        navigate(value ? `/learners?${value}` : '/learners');

        setLoading(true);

        return actions.getLearners(value ? { [value]: true } : undefined)
            .finally(() => {
                setLoading(false);
            });
    }, [navigate, actions]);

    const createLearner = useCallback(data => {
        return actions.createLearner(data)
            .finally(() => toggleFormOpen(false));
    }, [actions, toggleFormOpen]);

    const deleteLearner = useCallback(() => {
        return actions.deleteLearner(learner.id)
            .finally(() => {
                setLearner(null);
                toggleConfirmationDialogOpen(false);
            });
    }, [learner, actions, toggleConfirmationDialogOpen]);

    const handleEdit = useCallback(learner => {
        navigate(learner.url, { state: { edit: true } });
    }, [navigate]);

    const handleDelete = useCallback(learner => {
        setLearner(learner);
        toggleConfirmationDialogOpen(true);
    }, [toggleConfirmationDialogOpen]);

    if (!learners) return <LoadingIndicator />;

    return (
        <Page className="LearnersPage">
            <Page.Header
                title="Ученики"
                tabs={{
                    value: active ? 'active' : '',
                    items: [
                        {
                            value: '',
                            content: 'Все'
                        },
                        {
                            value: 'active',
                            content: 'Активные'
                        }
                    ],
                    onChange: handleTabsChange
                }}
                search={<UsersSearch params={{ role: 'learner' }} />}
                actions={[{
                    key: 'add',
                    icon: 'add',
                    content: 'Создать ученика',
                    color: 'primary',
                    variant: 'solid',
                    onClick: toggleFormOpen
                }]}
                loading={isLoading}
            />

            <Page.Content>

                <Page.Section
                    variant="outlined"
                    compact
                >
                    <LearnersTable
                        learners={learners}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                </Page.Section>
            </Page.Content>

            <FormDialog
                form="learner-form"
                title="Новый ученик"
                open={isFormOpen}
                onClose={toggleFormOpen}
            >
                <LearnerForm
                    id="learner-form"
                    onSubmit={createLearner}
                />
            </FormDialog>

            <ConfirmationDialog
                title="Подтвердите действие"
                message={`Вы действительно хотите удалить ученика ${learner?.fullname}?`}
                open={isConfirmationDialogOpen}
                onConfirm={deleteLearner}
                onClose={toggleConfirmationDialogOpen}
            />
        </Page>
    );
}